import { type ReactNode } from 'react';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';
import { css } from '@understory-io/leaf/css';
import { useIntl } from 'react-intl';
import { CloseIcon } from '~/ui/icons/close';

type LightboxProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Accessible name announced when the lightbox opens. */
  ariaLabel: string;
  /** Content rendered edge-to-edge inside the lightbox. */
  children: ReactNode;
};

export type { LightboxProps };

/**
 * A viewport-filling lightbox that hosts immersive content (map, gallery
 * image, media viewer). Edge-to-edge — no chrome around the content. The
 * floating close button at the top-right and the Escape key close it;
 * there is no click-outside-to-close, since the content fills the entire
 * viewport and there is no "outside" for the user to reach.
 *
 * Designed to be reused by any feature that needs a fullscreen view of a
 * single piece of content. The lightbox does not assume what's inside; the
 * caller controls layout, navigation, captions, etc.
 */
export function Lightbox({
  isOpen,
  onClose,
  ariaLabel,
  children,
}: LightboxProps) {
  const intl = useIntl();
  const closeLabel = intl.formatMessage({
    defaultMessage: 'Close',
    description: 'Close the lightbox',
  });
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      className={overlayStyle}
    >
      <Modal className={modalStyle}>
        <Dialog aria-label={ariaLabel} className={dialogStyle}>
          <div className={topBarStyle}>
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              className={closeButtonStyles}
            >
              <CloseIcon size="medium" />
            </button>
          </div>
          <div className={contentStyle}>{children}</div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

const overlayStyle = css({
  position: 'fixed',
  inset: 'none',
  zIndex: '[60]',
  background: 'surface.overlay',
});

const modalStyle = css({
  width: '[100vw]',
  height: '[100dvh]',
  background: 'surface.base',
  outline: 'none',
});

const dialogStyle = css({
  width: 'full',
  height: 'full',
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
});

// Top bar — fixed-height white strip holding the close button. Reserves
// space so the button is always reachable, even when the inner content
// has its own UI in the top-right (e.g. MapLibre zoom controls).
const topBarStyle = css({
  flexShrink: '0',
  display: 'flex',
  justifyContent: 'flex-end',
  paddingX: 'medium',
  paddingY: 'small',
  background: 'surface.base',
});

// Content area fills the remaining space below the top bar.
const contentStyle = css({
  flex: '1',
  minHeight: 'none',
  position: 'relative',
});

// Close button — transparent at rest, subtle bg fades in on hover. Full
// rounded so the hover bg renders as a circle around the icon.
const closeButtonStyles = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 'xlarge',
  height: 'xlarge',
  borderRadius: 'full',
  border: 'none',
  padding: 'none',
  background: 'surface.base',
  color: 'content.primary',
  cursor: 'interactive',
  transition: 'colors',
  _hover: {
    background: 'surface.neutral',
  },
});
